/**
 * Einsätze (Cares) — CRM-2.0-Endpunkte mit optimistischem Locking.
 *
 *   POST /v2/admin/cares       → Überlappung erlaubt; > 1 Tag: 409 E_CARE_OVERLAP_CONFIRM
 *   GET /v2/admin/cares/{id}
 *   PUT /v2/admin/cares/{id}   → 409 E_STALE_WRITE bei veraltetem `updated_at`
 *   DELETE /v2/admin/cares/{id} → nur im Status „Vorbereitung" (3), sonst 422
 *
 * Ersetzt in CRM 2.0 `v1Cares.update` (PUT /v1/admin/care/{id}); die v1-Route
 * bleibt für CRM 1.0 unverändert. Antwortform ist das v2-Envelope
 * `{ data, meta, errors }`; `data` ist der Einsatz inkl. `pb_name`/`bk_name`
 * und `updated_at`, `meta` trägt `startdatum`/`bk_startdatum` wie v1.
 */
import { apiV2 } from './api';
import { rethrowStaleWrite } from './optimisticLock';
import type { V1Care, V1CareCreatePayload, V1CareUpdatePayload } from './v1';
import type { CareOverlapInfo } from './careOverlap';

export interface V2Care extends V1Care {
  pb_name?: string | null;
  bk_name?: string | null;
}

export interface V2CareUpdateResult {
  care: V2Care;
  startdatum: string | null;
  bk_startdatum: string | null;
}

export interface V2CareCreateResult {
  care: V2Care;
  /** Überlappung mit einer anderen Betreuung (auch der stille Wechseltag), sonst null. */
  overlap: CareOverlapInfo | null;
  /**
   * Startdatum des PB nach dem Anlegen (YYYY-MM-DD). Die API setzt es
   * automatisch, wenn die Betreuung als „Bestätigt" angelegt wird.
   */
  startdatum: string | null;
}

export const v2Cares = {
  /**
   * POST /v2/admin/cares — legt eine Betreuung an. Überlappungen mit anderen
   * Betreuungen des PB sind erlaubt; ab mehr als einem gemeinsamen Tag
   * antwortet die API mit 409 `E_CARE_OVERLAP_CONFIRM` (s. lib/careOverlap),
   * bis `confirm_overlap: true` mitgeschickt wird.
   */
  create: async (payload: V1CareCreatePayload & { confirm_overlap?: boolean }): Promise<V2CareCreateResult> => {
    const res = await apiV2.post<{ data: V2Care; meta: { overlap?: CareOverlapInfo | null; startdatum?: string | null } }>(
      '/admin/cares',
      { origin: 'manuell', ...payload },
    );
    return {
      care: res.data.data,
      overlap: res.data.meta?.overlap ?? null,
      startdatum: res.data.meta?.startdatum ? String(res.data.meta.startdatum).slice(0, 10) : null,
    };
  },

  /**
   * DELETE /v2/admin/cares/{id} — löscht eine Betreuung im Status
   * „Vorbereitung". Andere Status lehnt die API mit 422 ab.
   */
  delete: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/cares/${id}`);
  },

  /** GET /v2/admin/cares/{id} */
  get: async (id: number): Promise<V2Care> => {
    const res = await apiV2.get<{ data: V2Care }>(`/admin/cares/${id}`);
    return res.data.data;
  },

  /**
   * PUT /v2/admin/cares/{id}
   * Wirft `StaleWriteError` (mit `current` = aktueller Einsatz), wenn der
   * gesendete `updated_at`-Stand nicht mehr aktuell ist.
   */
  update: async (id: number, payload: V1CareUpdatePayload): Promise<V2CareUpdateResult> => {
    try {
      const res = await apiV2.put<{
        data: V2Care;
        meta: { startdatum?: string | null; bk_startdatum?: string | null };
      }>(`/admin/cares/${id}`, payload);
      return {
        care: res.data.data,
        startdatum: res.data.meta?.startdatum ?? null,
        bk_startdatum: res.data.meta?.bk_startdatum ?? null,
      };
    } catch (e) {
      return rethrowStaleWrite<V2Care>(e);
    }
  },
};
